import React, { Suspense } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@givebutter/primitives/button';
import { Meter } from '@givebutter/primitives/meter';
import { Separator } from '@givebutter/primitives/separator';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal, maskWholeNumber, pluralize } from '@givebutter/util';
import { faPaperclip, faRefresh } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { refreshStats } from 'api/engage';
import { RecipientFilters } from 'assets/js/pages/engage/RecipientFilters';
import * as Row from 'assets/js/pages/engage/Row';
import { MessageDeliveryStats } from 'assets/js/pages/engage/components/Modals/MessageDeliveryStats';
import * as Dialog from 'components/dialog';
import { DialogSkeleton } from 'components/dialog-route';
import { MessageStatus as StatusFilters } from 'constants/messages';
import { useLocalStorage } from 'contexts/useLocalStorage';
import { useAppState } from 'customHooks/useAppState';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { isoDateFromServer } from 'helpers/dates';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import sanitizeHtml from 'sanitize-html';
import { twJoin } from 'tailwind-merge';
import { EngageMessage } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface MessageDetailsProps {
    message: EngageMessage;
}

const MessageDetails = (props: MessageDetailsProps) => {
    const { message } = props;
    const { success, error } = useToast();
    const { accountId, title } = useCurrentAccount();
    const queryClient = useQueryClient();
    const { apiUrl } = useAppState();
    const { messageIndexTab } = useLocalStorage(({ messageIndexTab }) => {
        return {
            messageIndexTab,
        };
    });

    const { mutate: refreshMessage, isPending: isLoading } = useMutation({
        mutationFn: () => refreshStats(accountId, message.id),
        onSuccess: () => {
            success('Message stats refreshed');
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.engageMessages(accountId),
            });
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Failed to refresh message stats');
        },
    });

    return (
        <div className={'flex flex-col gap-8 p-4 sm:max-w-none sm:flex-row sm:justify-between'}>
            <div className={'flex max-w-[24rem] flex-1 flex-col gap-4 md:max-w-none'}>
                <p className={'text-xl font-medium'}>
                    {message.revenue_generated !== null ? 'Revenue generated' : 'Details'}
                </p>
                {message.revenue_generated !== null && (
                    <p className="text-xl">${maskDecimal(message.revenue_generated)}</p>
                )}
                <div className={'flex flex-col gap-2'}>
                    {message.post?.campaign && (
                        <>
                            <Row.Root>
                                <Row.Title>Campaign:</Row.Title>
                                <Row.Value>{message.post.campaign.title}</Row.Value>
                            </Row.Root>
                            <Separator />
                        </>
                    )}
                    <Row.Root>
                        <Row.Title>To:</Row.Title>
                        <Row.Value style={{ overflowWrap: 'anywhere' }}>
                            {message.delivery_count === 1
                                ? `${message.delivery_count} contact`
                                : `${maskWholeNumber(message.delivery_count) || 0} contacts`}
                            <Dialog.Root>
                                <Dialog.Trigger
                                    type={'button'}
                                    className={'text-sm font-medium text-blue-500'}
                                >
                                    Contact list
                                </Dialog.Trigger>
                                <Dialog.Portal>
                                    <Dialog.Overlay />
                                    <Dialog.Content className="max-w-[48rem] px-6 py-4">
                                        <Dialog.Close />
                                        <Dialog.Title>Overview</Dialog.Title>
                                        <p className="my-6">
                                            You are viewing Engage statistics for{' '}
                                            <strong>{message.internal_name}</strong>.
                                        </p>
                                        <Suspense fallback={<DialogSkeleton />}>
                                            <MessageDeliveryStats message={message} />
                                        </Suspense>
                                    </Dialog.Content>
                                </Dialog.Portal>
                            </Dialog.Root>
                        </Row.Value>
                    </Row.Root>
                    <Separator />
                    <Row.Root>
                        <Row.Title>From:</Row.Title>
                        <Row.Value>
                            {(message.channel === 'email'
                                ? message.sender?.email
                                : message.sender?.value) || title}
                        </Row.Value>
                    </Row.Root>
                    <Separator />
                    {message.channel === 'email' && (
                        <>
                            <Row.Root>
                                <Row.Title>Subject:</Row.Title>
                                <Row.Value>{message.subject}</Row.Value>
                            </Row.Root>
                            <Separator />
                        </>
                    )}
                    <Row.Root>
                        <Row.Title>Filters:</Row.Title>
                        <Row.Value>
                            {message.recipient_filters && message.recipient_filters.length > 0 ? (
                                <RecipientFilters filters={message.recipient_filters} />
                            ) : (
                                <p>None</p>
                            )}
                        </Row.Value>
                    </Row.Root>
                    <Separator />
                    {messageIndexTab === StatusFilters.SENT && (
                        <>
                            <Row.Root>
                                <Row.Title>Sent At:</Row.Title>
                                <Row.Value>
                                    {message.sent_at
                                        ? isoDateFromServer(message.sent_at).toLocaleString(
                                              DateTime.DATETIME_SHORT_WITH_SECONDS,
                                          )
                                        : null}
                                </Row.Value>
                            </Row.Root>
                            <Separator />
                        </>
                    )}
                    {message.media.length > 0 && (
                        <Row.Root>
                            <Row.Title>{pluralize(message.media.length, 'Attachment')}:</Row.Title>
                            <Row.Value>
                                <div className={'flex flex-col gap-1'}>
                                    {message.media.map(media => (
                                        <div
                                            className={'flex items-center gap-1.5 text-sm'}
                                            key={media.id}
                                        >
                                            <FontAwesomeIcon
                                                size={'sm'}
                                                className={'text-gray-600'}
                                                icon={faPaperclip}
                                            />
                                            <a
                                                className={'break-all'}
                                                target={'_blank'}
                                                rel={'noreferrer'}
                                                href={media.url}
                                            >
                                                {media.name}
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </Row.Value>
                        </Row.Root>
                    )}
                    <Row.Root>
                        <Row.Title>Message:</Row.Title>
                        <Row.Value className="block break-words">
                            {message.channel === 'email' && (
                                <a
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ fontSize: '15px', lineHeight: '1.5' }}
                                    href={`${apiUrl}/dashboard/accounts/${accountId}/messages/${message.id}/preview`}
                                >
                                    View email
                                </a>
                            )}
                            {message.channel !== 'email' && (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizeHtml(message.body ?? ''),
                                    }}
                                />
                            )}
                        </Row.Value>
                    </Row.Root>
                </div>
            </div>
            <div className={'flex max-w-[20rem] flex-1 flex-col gap-4 md:max-w-none'}>
                <div className={'flex justify-between gap-4'}>
                    <p className={'text-xl font-medium'}>Statistics</p>
                    <Button
                        size={'sm'}
                        disabled={isLoading}
                        onClick={() => refreshMessage()}
                        variant={'outline'}
                        color={'neutral'}
                        className={'flex items-center gap-1.5'}
                    >
                        <FontAwesomeIcon
                            className={twJoin(isLoading && 'animate-spin')}
                            icon={faRefresh}
                        />
                        <p>Refresh</p>
                    </Button>
                </div>
                {message.stats && (
                    <React.Fragment>
                        <div
                            className={
                                'flex flex-col flex-wrap justify-between gap-4 md:flex-row md:p-8'
                            }
                        >
                            {message.channel === 'email' && (
                                <div className="flex flex-col items-center gap-2">
                                    <p>Opened</p>

                                    <p className="text-2xl font-medium">
                                        {maskWholeNumber(message.stats.unique_open_count)}
                                    </p>

                                    <Meter
                                        value={message.stats.open_rate}
                                        aria-label="Opened"
                                        type="circle"
                                        variant="success"
                                        size="md"
                                    />
                                </div>
                            )}
                            <div className="flex flex-col items-center gap-2">
                                <p>Bounced</p>
                                <p className="text-2xl font-medium">
                                    {maskWholeNumber(message.stats.bounce_count)}
                                </p>
                                <Meter
                                    value={message.stats.bounce_rate}
                                    aria-label="Bounced"
                                    type="circle"
                                    variant="warning"
                                    size="md"
                                />
                            </div>
                            {message.channel === 'sms' && (
                                <div className="flex flex-col items-center gap-2">
                                    <p>Sent</p>
                                    <p className="text-2xl font-medium">
                                        {maskWholeNumber(message.stats.sent_count)}
                                    </p>
                                    <Meter
                                        value={message.stats.sent_rate}
                                        aria-label="Sent"
                                        type="circle"
                                        variant="success"
                                        size="md"
                                    />
                                </div>
                            )}
                            <div className="flex flex-col items-center gap-2">
                                <p>Unsubscribed</p>
                                <p className="text-2xl font-medium">
                                    {maskWholeNumber(message.stats.unsubscribe_count)}
                                </p>
                                <Meter
                                    value={message.stats.unsubscribe_rate}
                                    aria-label="Unsubscribed Rate"
                                    type="circle"
                                    variant="error"
                                    size="md"
                                />
                            </div>
                        </div>
                        {message.channel === 'email' && (
                            <p className="block px-5 text-xs text-gray-500">
                                After 14 days, you'll need to manually refresh the statistics by
                                clicking <span className="font-medium">Refresh</span>.
                            </p>
                        )}
                    </React.Fragment>
                )}
            </div>
        </div>
    );
};

export { MessageDetails };
